import React, { useCallback, useEffect, useState, useRef } from "react";

import { FeedbakType, SKFeedback } from "../../models/SKFeedback";

import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import ContainerNavigation from "../../components/ContainerNavigation";

import { format } from "date-fns";
import ArtifactModerationDialog from "../contentModeration/ArtifactModerationDialog";
import ArtifactStateDialog, { DialoageContent } from "../artifacts/create/container/components/ArtifactStateDialog";
import APILoader from "../../components/APILoader";
import SKPagination, { PaginationObject } from "../../components/SKPagination";
import { Pagination } from "../../api/enums";
import TableHeader from "../../components/table/TableHeader";
import { useNavigationBar } from "../../context/NavigationContext";
import { fetchFeedback, getFeedbackCounts } from "../../api/feedback/feedbackServices";
import { formatAttributeValue } from "../../utils/stringFunctions";
import { QueryDocumentSnapshot } from "firebase/firestore";

type SKFeedbackKey = keyof SKFeedback;

interface SKFeedbackTableHeader {
  key: SKFeedbackKey;
  label: string;
  span: number;
  sortable: boolean;
}

const ListFeedback = () => {
  const [listCount, setListCount] = useState<Record<FeedbakType, number>>({
    [FeedbakType.issue]: 0,
    [FeedbakType.suggestion]: 0,
  });

  let { showChildRouteAsModal } = useNavigationBar();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [feedbacks, setfeedbacks] = useState<SKFeedback[] | null>([]);

  // Get selected type from URL params, default to issue
  const [selectedType, setSelectedType] = useState<FeedbakType>(() => {
    const tabParam = searchParams.get("tab");
    return tabParam === "suggestion" ? FeedbakType.suggestion : FeedbakType.issue;
  });

  const [loading, setLoading] = useState(false);
  const [dialogContent, setDialogContent] = useState<DialoageContent | null>(null);
  const location = useLocation();

  const [paginationObject, setPaginationObject] = useState<PaginationObject | null>(null);
  const [firstDocumentId, setFirstDocumentId] = useState<string | undefined>(undefined);

  // Ref to track if component is mounted and prevent unnecessary re-fetching
  const isMountedRef = useRef(false);
  const lastFetchParamsRef = useRef<{ selectedType: FeedbakType; sortConfig: any } | null>(null);

  // Initialize sort config from URL params
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" } | undefined>(() => {
    const sortKey = searchParams.get("sortKey");
    const sortDirection = searchParams.get("sortDirection") as "asc" | "desc";
    return sortKey && sortDirection ? { key: sortKey, direction: sortDirection } : undefined;
  });

  // Get current page from URL params
  const [currentPage, setCurrentPage] = useState<number>(() => {
    const pageParam = searchParams.get("page");
    return pageParam ? parseInt(pageParam, 10) : 1;
  });

  const heads = (feedbackType: FeedbakType = FeedbakType.issue): SKFeedbackTableHeader[] => {
    return [
      { key: "id", label: `${feedbackType} ID`, span: 2, sortable: false },
      { key: "feedbackDate", label: "Submitted", span: 2, sortable: false },
      { key: "type", label: "Type", span: 2, sortable: false },
      { key: "category", label: "Category", span: 2, sortable: false },
      { key: "phone", label: "User Phone", span: 2, sortable: false },
      { key: "osVersion", label: "OS Version", span: 1, sortable: false },
      { key: "appVersion", label: "App Version", span: 1, sortable: false },
    ];
  };

  const [headers, setHeaders] = useState(heads);

  useEffect(() => {
    setHeaders(heads(selectedType));
  }, [selectedType]);

  useEffect(() => {
    isMountedRef.current = true;
    showChildRouteAsModal(true);
    return () => {
      isMountedRef.current = false;
      showChildRouteAsModal(false);
    };
  }, []);

  // Update URL when state changes
  useEffect(() => {
    const newParams = new URLSearchParams();
    const newTab = selectedType === FeedbakType.suggestion ? "suggestion" : "issue";
    newParams.set("tab", newTab);

    if (sortConfig) {
      newParams.set("sortKey", sortConfig.key);
      newParams.set("sortDirection", sortConfig.direction);
    }

    if (currentPage > 1) {
      newParams.set("page", currentPage.toString());
    }

    setSearchParams(newParams, { replace: true });
  }, [selectedType, sortConfig, currentPage, setSearchParams]);

  // Handle tab change
  const handleTabChange = (newType: FeedbakType) => {
    setSelectedType(newType);
    // Reset pagination when changing tabs
    setPaginationObject(null);
    setFirstDocumentId(undefined);
    setCurrentPage(1);
  };

  const fetchCount = async () => {
    let counts = await getFeedbackCounts();
    setListCount(counts);
  };

  useEffect(() => {
    fetchCount();
  }, []);

  const fetchData = async (selectedType: FeedbakType, moveTo: Pagination) => {
    try {
      let pageSize = 10;
      setLoading(true);
      setfeedbacks(null);

      let pagination: PaginationObject = {
        firstDoc: undefined,
        lastDoc: undefined,
        hasNext: false,
        hasPrev: false,
      };
      let currentDoc = undefined;

      switch (moveTo) {
        case Pagination.First:
          currentDoc = undefined;
          setCurrentPage(1);
          break;
        case Pagination.Previous:
          currentDoc = paginationObject?.firstDoc;
          setCurrentPage(prev => Math.max(1, prev - 1));
          break;
        case Pagination.Last:
          currentDoc = paginationObject?.lastDoc;
          // For last page, we need to calculate how many items are on the last page
          const totalItems = listCount[selectedType];
          const itemsPerPage = 10;
          const lastPageSize = totalItems % itemsPerPage || itemsPerPage;
          pageSize = lastPageSize;
          // Calculate last page
          const totalPages = Math.ceil(totalItems / itemsPerPage);
          setCurrentPage(totalPages);
          break;
        case Pagination.Next:
          currentDoc = paginationObject?.lastDoc;
          setCurrentPage(prev => prev + 1);
          break;
      }

      const { docs: editReviewedDocs, ...editRevewRest } = await fetchFeedback(selectedType, pageSize, currentDoc, moveTo, sortConfig);
      pagination = editRevewRest;
      setfeedbacks(editReviewedDocs);

      if (moveTo === Pagination.First || (pagination.firstDoc && pagination.firstDoc.id === firstDocumentId)) {
        setFirstDocumentId(pagination.firstDoc?.id);
        pagination.hasPrev = false;
      } else if (moveTo === Pagination.Previous && pagination.firstDoc && pagination.firstDoc.id === firstDocumentId) {
        pagination.hasPrev = false;
      }
      setPaginationObject(pagination);
      console.log("Pagination object set:", pagination);
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch data if component is mounted and parameters have actually changed
    const currentParams = { selectedType, sortConfig };
    const lastParams = lastFetchParamsRef.current;

    // Check if parameters have actually changed
    const hasParamsChanged =
      !lastParams || lastParams.selectedType !== selectedType || JSON.stringify(lastParams.sortConfig) !== JSON.stringify(sortConfig);

    if (hasParamsChanged) {
      lastFetchParamsRef.current = currentParams;
      fetchData(selectedType, Pagination.First);
    }
  }, [selectedType, sortConfig]);

  const removeFlaggedItemLocally = (contentId: string) => {
    let items = feedbacks!.filter(item => item.id !== contentId);
    setfeedbacks(items);
  };

  const handleSortChange = (config: { key: string; direction: "asc" | "desc" } | undefined) => {
    console.log("Sort Config:", config);
    setSortConfig(config);
    // Reset pagination when sorting changes
    setPaginationObject(null);
    setFirstDocumentId(undefined);
    setCurrentPage(1);
  };

  const getFormattedValue = (data: SKFeedback, key: SKFeedbackKey) => {
    switch (key) {
      case "feedbackDate":
        return formatAttributeValue(data[key]);
      default:
        return formatAttributeValue(data[key]);
    }
  };

  return (
    <div className="flex-1 bg-white">
      <div className="flex flex-col flex-1">
        <ContainerNavigation
          backArrow={false}
          title={"Feedback Inbox"}
          buttonTitle={"Create"}
          subtitle={"User Reported"}
          enableLink={true}
          onSearch={() => {}}
        />

        <div className="flex flex-row gap-4 px-2  mt-[30px] overflow-x-auto">
          <Link
            to={"#"}
            onClick={e => {
              e.preventDefault();
              handleTabChange(FeedbakType.issue);
            }}
            className={
              (selectedType === FeedbakType.issue ? " border-black " : " border-white ") +
              "border-b-2 text-lg font-bold font flex items-center dark:text-black text-nowrap"
            }
          >
            Issue . {listCount[FeedbakType.issue]}
          </Link>
          <Link
            to={"#"}
            onClick={e => {
              e.preventDefault();
              handleTabChange(FeedbakType.suggestion);
            }}
            className={
              (selectedType === FeedbakType.suggestion ? " border-black " : " border-white ") +
              "border-b-2 text-lg font-bold font flex items-center dark:text-black text-nowrap"
            }
          >
            Suggestion . {listCount[FeedbakType.suggestion]}
          </Link>
        </div>

        {/* Page indicator */}
        {currentPage > 1 && (
          <div className="px-2 mt-2 text-sm text-gray-600">
            Page {currentPage} of {Math.ceil(listCount[selectedType] / 10)}
          </div>
        )}

        <div className="flex flex-col p-2 overflow-y-auto h-[calc(100vh-354px)]">
          <TableHeader headers={headers} onSortChange={handleSortChange} className="grid-cols-12 w-full" />

          <div className="overflow-y-scroll">
            <APILoader loading={loading} />

            {feedbacks?.map(content => {
              return (
                <div
                  key={content.id}
                  className="bg-white group hover:cursor-pointer hover:font-semibold hover:bg-skseparatorgray"
                  onClick={() => navigate(`${content.type}/${content.id}`)}
                >
                  <div className="hidden md:block">
                    <div className="relative border-[1px] md:border-0 rounded-xl md:mb-0 md:rounded-none">
                      <div className=" grid items-center grid-cols-12 w-full gap-4 border-b-[1px] border-gray-300 py-3 ">
                        {headers.map(header => (
                          <div key={header.key} className={`col-span-${header.span} text-sm font-bold capitalize md:text-xs md:font-normal`}>
                            {getFormattedValue(content, header.key)}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {(() => {
          const shouldShowPagination = paginationObject?.hasNext || paginationObject?.hasPrev;
          console.log("Pagination UI condition:", {
            paginationObject,
            hasNext: paginationObject?.hasNext ?? false,
            hasPrev: paginationObject?.hasPrev ?? false,
            shouldShow: shouldShowPagination,
          });
          return shouldShowPagination;
        })() && (
          <div className="content-center items-center mx-auto">
            <SKPagination
              onFirst={() => {
                fetchData(selectedType, Pagination.First);
              }}
              onLast={() => {
                fetchData(selectedType, Pagination.Last);
              }}
              onNext={() => {
                fetchData(selectedType, Pagination.Next);
              }}
              onPrev={() => {
                fetchData(selectedType, Pagination.Previous);
              }}
              hasNext={paginationObject?.hasNext || false}
              hasPrev={paginationObject?.hasPrev || false}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ListFeedback;
